import React, { useState } from "react";
import { useProfilePageData, useProfilePageHandlers } from "../hooks";
import {
  PROFILE_MENU_ITEMS,
  WITHDRAWAL_WARNINGS,
  ProfileTab,
} from "../constants";
import { SignupForm } from "@/features/signup";
import { MyQuestionsSection } from "@/features/profile";
import { TransactionList } from "@/features/subscription/payment-history";
import {
  SubscriptionCard,
  BillingHistoryList,
  PaymentFailureAlert,
  useBillingHistory,
  useSubscription,
} from "@/features/subscription/subscription-manage";

/**
 * ProfilePageWidget - 마이페이지 렌더링
 * FSD: widgets/profile/ui
 *
 * 역할: 프로필 관련 여러 features를 조합하는 UI 렌더링만 담당
 * 로직은 hooks로 분리
 */
export const ProfilePageWidget = () => {
  const [activeTab, setActiveTab] = useState<ProfileTab>("subscription");
  const { profileData, isLoading, initialData } = useProfilePageData();
  const { handleEditSubmit, handleWithdraw } = useProfilePageHandlers(() =>
    setActiveTab("subscription")
  );

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="text-center">
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* 모바일 탭 네비게이션 */}
        <div className="lg:hidden border-b border-gray-200">
          <div className="overflow-x-auto scrollbar-hide">
            <nav className="flex gap-0">
              {PROFILE_MENU_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === item.id
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-0">
            {/* 데스크톱 사이드바 메뉴 */}
            <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

            {/* 메인 콘텐츠 */}
            <Content
              activeTab={activeTab}
              initialData={initialData}
              onEditSubmit={handleEditSubmit}
              onEditCancel={() => setActiveTab("subscription")}
              onWithdraw={handleWithdraw}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface SidebarProps {
  activeTab: ProfileTab;
  onTabChange: (tab: ProfileTab) => void;
}

const Sidebar = ({ activeTab, onTabChange }: SidebarProps) => (
  <div className="hidden lg:block w-56 flex-shrink-0 bg-gray-100 p-8">
    <h2 className="text-2xl font-bold text-black mb-8">마이페이지</h2>
    <nav className="space-y-6">
      {PROFILE_MENU_ITEMS.map((item) => (
        <button
          key={item.id}
          onClick={() => onTabChange(item.id)}
          className={`block w-full text-left px-0 py-0 font-medium text-base transition-all ${
            activeTab === item.id
              ? "text-black border-b-2 border-black pb-2"
              : "text-gray-700 hover:text-black"
          }`}
        >
          {item.label}
        </button>
      ))}
    </nav>
  </div>
);

interface ContentProps {
  activeTab: ProfileTab;
  initialData: any;
  onEditSubmit: (data: any) => void;
  onEditCancel: () => void;
  onWithdraw: (password: string) => void;
}

const Content = ({
  activeTab,
  initialData,
  onEditSubmit,
  onEditCancel,
  onWithdraw,
}: ContentProps) => (
  <div className="flex-1 bg-gray-50 w-full">
    {activeTab === "subscription" && <SubscriptionSection />}

    {activeTab === "payment" && (
      <div className="p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          결제 내역
        </h2>
        <TransactionList />
      </div>
    )}

    {activeTab === "inquiry" && (
      <div className="p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          내 문의
        </h2>
        <MyQuestionsSection />
      </div>
    )}

    {activeTab === "edit" && (
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          회원 정보 수정
        </h2>
        <SignupForm
          type="individual"
          mode="edit"
          onSubmit={onEditSubmit}
          onBack={onEditCancel}
          initialData={initialData}
        />
      </div>
    )}

    {activeTab === "withdraw" && (
      <WithdrawSection warnings={WITHDRAWAL_WARNINGS} onWithdraw={onWithdraw} />
    )}
  </div>
);

interface TabContentProps {
  title: string;
  empty: string;
}

const TabContent = ({ title, empty }: TabContentProps) => (
  <div className="p-4 md:p-6 lg:p-8">
    <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
      {title}
    </h2>
    <div className="text-center py-8 md:py-12">
      <p className="text-gray-500 text-sm md:text-base">{empty}</p>
    </div>
  </div>
);

interface WithdrawSectionProps {
  warnings: string[];
  onWithdraw: (password: string) => void;
}

const WithdrawSection = ({ warnings, onWithdraw }: WithdrawSectionProps) => {
  const [password, setPassword] = React.useState("");
  const [isAgreed, setIsAgreed] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState("");

  const handleWithdrawClick = () => {
    setPasswordError("");

    if (!password) {
      setPasswordError("비밀번호를 입력해주세요.");
      return;
    }

    if (!isAgreed) {
      setPasswordError("위 내용을 모두 확인했음에 동의해주세요.");
      return;
    }

    onWithdraw(password);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-black mb-2">
        회원 탈퇴
      </h2>
      <p className="text-gray-600 text-xs md:text-sm mb-4 md:mb-6">
        회원 탈퇴는 되돌릴 수 없습니다. 신중하게 선택해주세요.
      </p>
      <div className="bg-red-50 border border-red-200 rounded p-4 md:p-6 mb-4 md:mb-6">
        <h3 className="font-bold text-red-600 mb-2 md:mb-3 text-sm md:text-base">
          탈퇴 전 확인사항
        </h3>
        <ul className="text-xs md:text-sm text-gray-700 space-y-1 md:space-y-2">
          {warnings.map((warning, i) => (
            <li key={i}>• {warning}</li>
          ))}
        </ul>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          현재 비밀번호
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError("");
          }}
          placeholder="비밀번호를 입력해주세요."
          className="w-full md:w-96 px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded text-xs md:text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600"
        />
        {passwordError && (
          <p className="text-red-600 text-xs md:text-sm mt-2">
            {passwordError}
          </p>
        )}
      </div>
      <div className="mb-6 flex items-start gap-3">
        <input
          type="checkbox"
          id="agree-withdraw"
          checked={isAgreed}
          onChange={(e) => {
            setIsAgreed(e.target.checked);
            setPasswordError("");
          }}
          className="mt-1 w-4 h-4"
        />
        <div className="flex-1">
          <label htmlFor="agree-withdraw" className="cursor-pointer">
            <p className="text-xs md:text-sm font-medium text-gray-900">
              위 내용을 모두 확인 하였습니다.
            </p>
            <p className="text-xs md:text-sm text-gray-500">
              GGP에서 탈퇴하겠습니다.
            </p>
          </label>
        </div>
      </div>
      <button
        onClick={handleWithdrawClick}
        className="w-full md:w-auto px-6 md:px-8 py-2 md:py-3 bg-red-600 text-white text-sm md:text-base rounded font-medium hover:bg-red-700"
      >
        회원 탈퇴
      </button>
    </div>
  );
};

const SubscriptionSection = () => {
  const { data: subscription, isLoading, error } = useSubscription();
  const { data: billingHistory } = useBillingHistory(subscription?.id);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          내 구독 현황
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-500">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          내 구독 현황
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
          내 구독 현황
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-500">구독 현황이 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-6">
        내 구독 현황
      </h2>

      <SubscriptionCard subscription={subscription} />

      {subscription.last_payment_failed_date && (
        <div className="mt-6">
          <PaymentFailureAlert subscription={subscription} />
        </div>
      )}

      {billingHistory && billingHistory.billing_history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-black mb-4">결제 내역</h3>
          {subscription.next_billing_date && (
            <p className="text-sm text-gray-600 mb-4">
              다음 결제 예정일: <strong>{new Date(subscription.next_billing_date).toLocaleDateString()}</strong>
            </p>
          )}
          <BillingHistoryList history={billingHistory.billing_history} />
        </div>
      )}
    </div>
  );
};
